// Libs
import classNames from 'classnames/bind';
import { ChangeEvent, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { TFunction } from 'i18next';
import { pdf } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { PickSlipPdf } from '~/components/module/job';
import { BaseCheckbox, BaseTable, Loading, UnderDevelopment } from '~/components/common';
// Others
import { IHeaderCellType } from '~/utils/interface/common';
import {
  IDetailJob,
  IJobAddOn,
  IJobItem,
  IParamQueryJobDetail,
  IPickSlipPdf,
  IQueryParamJobItem,
  ISubRental,
  ISubRentalJob,
} from '~/utils/interface/jobs';
import { getFullName } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IPaginationResponse } from '~/utils/interface/base';
import { getDetailJob, getListJobItem } from '~/thunks/job/jobThunk';
import { TypeJobDetailEnum, TypeListInventory } from '~/utils/enum/common';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE, EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './PickSlipFormModal.module.scss';
import { icons } from '~/assets';

type Props = {
  jobId: string;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const columns = (t: TFunction, jobSubRentals: ISubRental[]): IHeaderCellType<IJobItem>[] => {
  return [
    {
      id: 'status',
      width: 380,
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('pick_slip_label_status')}</Box>
        </Stack>
      ),
      render: (record: IJobItem) => (
        <Box className={cx('viewCol')}>
          <Box className={cx('viewCells')}>
            <Box className={cx('viewCell')}>
              <BaseCheckbox disabled label={t('pick_slip_label_loaded')} checked={record?.isLoaded} />

              {record?.isLoaded && (
                <Typography className={cx('textName')}>
                  {getFullName({
                    firstName: record?.loadedBy?.firstName,
                    lastName: record?.loadedBy?.lastName,
                  })}
                </Typography>
              )}
            </Box>
            <Box className={cx('viewCell')}>
              <BaseCheckbox disabled label={t('pick_slip_label_delivery')} checked={record?.isDelivery} />

              {record?.isDelivery && (
                <Typography className={cx('textName')}>
                  {getFullName({
                    firstName: record?.deliveryBy?.firstName,
                    lastName: record?.deliveryBy?.lastName,
                  })}
                </Typography>
              )}
            </Box>
            <Box className={cx('viewCell')}>
              <BaseCheckbox disabled label={t('pick_slip_label_pickup')} checked={record?.isPickup} />

              {record?.isPickup && (
                <Typography className={cx('textName')}>
                  {getFullName({
                    firstName: record?.pickupBy?.firstName,
                    lastName: record?.pickupBy?.lastName,
                  })}
                </Typography>
              )}
            </Box>
          </Box>

          {record?.jobAddOns?.length > 0 &&
            record.jobAddOns.map((item, idx) => (
              <Box key={`status-${idx}`} className={cx('viewCells')}>
                <Box className={cx('viewCell')}>
                  <BaseCheckbox disabled label={t('pick_slip_label_loaded')} checked={item?.isLoaded} />

                  {item?.isLoaded && (
                    <Typography className={cx('textName')}>
                      {getFullName({
                        firstName: item?.loadedBy?.firstName,
                        lastName: item?.loadedBy?.lastName,
                      })}
                    </Typography>
                  )}
                </Box>
                <Box className={cx('viewCell')}>
                  <BaseCheckbox disabled label={t('pick_slip_label_delivery')} checked={item?.isDelivery} />

                  {item?.isDelivery && (
                    <Typography className={cx('textName')}>
                      {getFullName({
                        firstName: item?.deliveryBy?.firstName,
                        lastName: item?.deliveryBy?.lastName,
                      })}
                    </Typography>
                  )}
                </Box>
                <Box className={cx('viewCell')}>
                  <BaseCheckbox disabled label={t('pick_slip_label_pickup')} checked={item?.isPickup} />

                  {item?.isPickup && (
                    <Typography className={cx('textName')}>
                      {getFullName({
                        firstName: item?.pickupBy?.firstName,
                        lastName: item?.pickupBy?.lastName,
                      })}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
        </Box>
      ),
    },
    {
      id: 'quantity',
      label: (
        <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} gap={2}>
          {t('pick_slip_label_quantity')}
        </Stack>
      ),
      render: (record: IJobItem) => {
        const isPick = record?.isPickup || record?.isDelivery || record?.isLoaded;

        return (
          <Box className={cx('viewCol')}>
            <Box className={cx('viewCells')}>
              <Typography className={cx('textQty')} height={isPick ? '35.5px' : undefined}>
                {record?.quantity}
              </Typography>
            </Box>

            {record?.jobAddOns?.length > 0 &&
              record.jobAddOns.map((item, idx) => {
                const isPickItem = item?.isPickup || item?.isDelivery || item?.isLoaded;

                return (
                  <Box key={`quantity-${idx}`} className={cx('viewCells')}>
                    <Typography
                      height={isPickItem ? '35.5px' : undefined}
                      key={`quantity-${idx}`}
                      className={cx('textQty')}
                    >
                      {item?.quantity}
                    </Typography>
                  </Box>
                );
              })}
          </Box>
        );
      },
    },
    {
      id: 'type',
      label: (
        <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} gap={2}>
          {t('pick_slip_label_type')}
        </Stack>
      ),
      render: (record: IJobItem) => {
        const isPick = record?.isPickup || record?.isDelivery || record?.isLoaded;

        return (
          <Box className={cx('viewCol')}>
            <Box className={cx('viewCells')}>
              <Typography className={cx('textValue')} height={isPick ? '35.5px' : undefined}>
                {record?.type === TypeListInventory.PACKAGE ? t('form_orders_label_package') : t('order_pdf_product')}
              </Typography>
            </Box>

            {record?.jobAddOns?.length > 0 &&
              record.jobAddOns.map((item, idx) => {
                const isPickItem = item?.isPickup || item?.isDelivery || item?.isLoaded;

                return (
                  <Box key={`quantity-${idx}`} className={cx('viewCells')}>
                    <Typography
                      height={isPickItem ? '35.5px' : undefined}
                      key={`quantity-${idx}`}
                      className={cx('textValue')}
                    >
                      {record?.type === TypeListInventory.PACKAGE && item?.type === TypeListInventory.PRODUCT
                        ? t('order_detail_table_package_sub')
                        : t('order_detail_table_add_on_sub')}
                    </Typography>
                  </Box>
                );
              })}
          </Box>
        );
      },
    },
    {
      id: 'name',
      width: 400,
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          {t('pick_slip_label_item')}
        </Stack>
      ),
      render: (record: IJobItem) => {
        const isPick = record?.isPickup || record?.isDelivery || record?.isLoaded;
        const subrental = jobSubRentals?.find((item) => item?.inventoryId === record?.inventoryId);

        return (
          <Box className={cx('viewCol')}>
            <Box className={cx('viewCellsItemName')}>
              <Typography className={cx('textValue')} height={isPick ? '35.5px' : undefined}>
                {record?.name}
              </Typography>
              {subrental && (
                <Typography className={cx('textValue')} height={isPick ? '35.5px' : undefined}>
                  {`Sub rental / ${subrental?.vendorName} / ${subrental?.quantity}`}
                </Typography>
              )}
            </Box>

            {record?.jobAddOns?.length > 0 &&
              record.jobAddOns.map((itemAddOn, idx) => {
                const isPickItem = itemAddOn?.isPickup || itemAddOn?.isDelivery || itemAddOn?.isLoaded;
                const subrental = jobSubRentals?.find((item) => item?.inventoryId === itemAddOn?.inventoryId);

                return (
                  <Box key={`name-${idx}`} className={cx('viewCellsItemName')}>
                    <Typography
                      height={isPickItem ? '35.5px' : undefined}
                      key={`quantity-${idx}`}
                      className={cx('textValue')}
                    >
                      {itemAddOn?.name}
                    </Typography>

                    {subrental && (
                      <Typography
                        height={isPickItem ? '35.5px' : undefined}
                        key={`quantity-${idx}`}
                        className={cx('textValue')}
                      >
                        {`Sub rental / ${subrental?.vendorName} / ${subrental?.quantity}`}
                      </Typography>
                    )}
                  </Box>
                );
              })}
          </Box>
        );
      },
    },
  ];
};

const PickSlipFormModal = (props: Props) => {
  //#region Destructuring Props
  const { jobId, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [listJob, setListJob] = useState<IJobItem[]>([]);
  const [jobSubRentals, setJobSubRentals] = useState<ISubRental[]>([]);
  const [paramObject, setParamObject] = useState<IQueryParamJobItem>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
    jobId: jobId,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetJobItem(paramObject);
  }, [paramObject]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailJob = async () => {
    setIsLoading(true);

    const params: IParamQueryJobDetail = {
      jobId,
      type: TypeJobDetailEnum.OVERVIEW,
    };

    try {
      const res = await dispatch(getDetailJob(params)).unwrap();
      if (!res?.data) return null;

      return res.data as IDetailJob;
    } catch (e) {
      setIsLoading(false);
      return null;
    }
  };

  const handleGetJobItem = (paramObject: IQueryParamJobItem) => {
    setIsLoading(true);

    dispatch(getListJobItem(paramObject))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { jobSubRentals, pagination, responses } = res?.data;

        setListJob(responses);
        setPagination(pagination);
        setJobSubRentals(jobSubRentals);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleCloseModal = () => {
    onClose && onClose();
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };

  const handlePrintPickSlip = async () => {
    const detailJob = await handleGetDetailJob();

    if (!detailJob) return;

    const { jobItems, jobAddOns } = convertJobDataWithSubRental(
      listJob || [],
      detailJob?.jobAddOns || [],
      detailJob?.subRentals || []
    );

    const data: IPickSlipPdf = {
      client: detailJob?.client,
      startDate: detailJob?.startDate || EMPTY_STRING,
      startTime: detailJob?.startTime || EMPTY_STRING,
      endDate: detailJob?.endDate || EMPTY_STRING,
      endTime: detailJob?.endTime || EMPTY_STRING,
      jobItems,
      jobAddOns,
      total: [...detailJob?.jobItems, ...detailJob?.jobAddOns]
        .reduce((total, item) => total + Number(item?.total || 0) || 0, 0)
        .toFixed(2)
        .toString(),
      orderId: detailJob?.orderId || EMPTY_STRING,
      internalNote: detailJob?.internalNote || EMPTY_STRING,
      externalNote: detailJob?.externalNote || EMPTY_STRING,
    };

    const pdfBlob = await generatePdfBlob(data);

    setIsLoading(false);

    if (pdfBlob instanceof Blob) {
      const url = URL.createObjectURL(pdfBlob);
      const newTab = window.open(url, '_blank', 'noopener,noreferrer');
      if (newTab) newTab.opener = null;

      setTimeout(() => URL.revokeObjectURL(url), 10000);
    }
  };

  const convertJobDataWithSubRental = (
    jobItems: IJobItem[] = [],
    jobAddOns: IJobAddOn[] = [],
    subRentals: ISubRentalJob[] = []
  ) => {
    const subRentalMap = subRentals.reduce<Record<string, ISubRentalJob>>((acc, sub) => {
      acc[String(sub.inventoryId)] = sub;
      return acc;
    }, {});

    const formatPrice = (price: number) => {
      return `$${price.toFixed(2)}`;
    };

    const applySubRentalName = (item: any) => {
      const subRental = subRentalMap[String(item.inventoryId)];

      if (!subRental) return item;

      return {
        ...item,
        name: `${item.name} / Sub Rental / ${subRental.quantity} - ${formatPrice(subRental.price)}`,
      };
    };

    const convertedItems = jobItems.map((item) => ({
      ...applySubRentalName(item),
      jobAddOns: item.jobAddOns?.map((addOn) => applySubRentalName(addOn)) || [],
    }));

    const convertedAddOns = jobAddOns.map((addOn) => applySubRentalName(addOn));

    return {
      jobItems: convertedItems,
      jobAddOns: convertedAddOns,
    };
  };

  const generatePdfBlob = async (data: IPickSlipPdf): Promise<Blob | null> => {
    try {
      return await pdf(<PickSlipPdf dataPdf={data} />).toBlob();
    } catch (error) {
      console.error('Generate PDF failed: ', error);
      return null;
    }
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='pickSlipFormModalComponent'
      aria-labelledby='pickSlipFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'lg'}
    >
      <DialogTitle id='pickSlipFormModal' className={cx('header')}>
        <Typography className={cx('title')}>{t('pick_slip_form_title', { jobId: jobId })}</Typography>

        <Stack direction={'row'} alignItems={'center'} gap={4}>
          <Button onClick={handlePrintPickSlip} variant='contained'>
            {t('common_text_print', { ns: 'common/base' })}
          </Button>

          <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
            <Avatar
              src={icons.commonIconCancel}
              className={cx('iconClose')}
              alt={t('common_alt_img', { ns: 'common/base' })}
            />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <BaseTable
          borderRadius={0}
          dataSource={listJob}
          currentPage={paramObject.page}
          totalPage={pagination?.totalPages}
          columns={columns(t, jobSubRentals)}
          onPageChange={handlePageChange}
        />
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>
      </DialogActions>
      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Dialog>
  );
};

export default PickSlipFormModal;
